import DocsSiteSource from '@modules/eventStream/enum/DocsSiteSource';
import LoggedPage from '@modules/eventStream/enum/LoggedPage';
import LoggedPageSources from '@modules/eventStream/enum/LoggedPageSources';
import getReferrer from '@modules/eventStream/utils/getReferrer';
import { useMediaQuery } from '@rbx/ui';
import safeLocalStorage from '@modules/utilities/utils/safeLocalStorage';
import { useCallback, useEffect, useMemo, useState } from 'react';

const RobloxCreatorPageLoadTimeStorageName = (
  pageName: string,
  source: DocsSiteSource | 'pageLoad' = 'pageLoad',
) => `CreatorHub.${pageName}.${source}`;

const getTime = () => new Date().getTime();

const pageRefreshSource = 'pageLoad';
/**
 *
 * @param page
 * @param altSources alternative entry point sources to this page, besides a page reload
 *        NOTE: invoke logEnterEntryPoint(src) on each source in altSources at point of entry
 * @returns
 */
function usePageLogs(page: LoggedPage) {
  const avgMobilePageLoadTime = 8.6 * 1000;
  const avgDesktopPageLoadTime = 2.5 * 1000;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('Medium'));
  const maxNavWaitTime = useMemo(
    () => 1.2 * (isMobile ? avgMobilePageLoadTime : avgDesktopPageLoadTime),
    [isMobile, avgMobilePageLoadTime, avgDesktopPageLoadTime],
  );
  const getPageLoadTime = useCallback(
    (source: DocsSiteSource | 'pageLoad' = pageRefreshSource): number | undefined => {
      const val = safeLocalStorage.getItem(RobloxCreatorPageLoadTimeStorageName(page, source));
      if (!val) return undefined;
      return Number(val);
    },
    [page],
  );

  const logEnterEntryPoint = useCallback(
    (source: DocsSiteSource | 'pageLoad' = pageRefreshSource) => {
      safeLocalStorage.setItem(
        RobloxCreatorPageLoadTimeStorageName(page, source),
        new Date().getTime().toString(),
      );
    },
    [page],
  );

  const [isPageRefresh, setIsPageRefresh] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [lastLoggedEntryPoint, setLastLoggedEntryPoint] = useState<DocsSiteSource | string>();

  useEffect(() => {
    const navigationApiSupported = !!window?.performance?.navigation;
    const onBeforeWindowUnload = () => {
      logEnterEntryPoint();
    };
    const now = getTime();
    let isPageRefreshVal;
    if (navigationApiSupported) {
      isPageRefreshVal = performance.navigation.type === performance.navigation.TYPE_RELOAD;
    } else {
      const lastRefreshTime = getPageLoadTime();
      isPageRefreshVal = lastRefreshTime && now - lastRefreshTime < maxNavWaitTime;
    }
    let source = isPageRefreshVal
      ? DocsSiteSource.PageReload
      : getReferrer() || DocsSiteSource.DirectURLVisit;
    // is not page refresh if loaded from an alternative source
    const altSources = LoggedPageSources[page];
    let i = 0;
    let done = false;
    while (i < altSources.length && !done) {
      const src = altSources[i];
      if (src !== DocsSiteSource.PageReload) {
        const sourceClickedTime = getPageLoadTime(src);
        done = !!(sourceClickedTime && now - sourceClickedTime < maxNavWaitTime);
        if (done) {
          isPageRefreshVal = false;
          source = src;
        }
      }
      i += 1;
    }
    setIsPageRefresh(!!isPageRefreshVal);
    setLastLoggedEntryPoint(source);
    window.addEventListener('beforeunload', onBeforeWindowUnload);
    setIsReady(true);
    return () => {
      if (!navigationApiSupported) {
        window.removeEventListener('beforeunload', onBeforeWindowUnload);
      }
    };
  }, [page, getPageLoadTime, logEnterEntryPoint, maxNavWaitTime]);

  // for each logged page source, sources should be logged at entry point enter
  // via usePageLogs#logEnterEntryPoint
  return { isReady, isPageRefresh, logEnterEntryPoint, lastLoggedEntryPoint };
}

export default usePageLogs;
